import {
  AlertCircle,
  BarChart3,
  CheckCircle2,
  GripVertical,
  Mail,
  Plus,
  Settings,
  ShieldAlert,
  Trash2,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { HelpSupportButton } from '@/components/help-support';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { DataModeBadge } from '@/components/DataModeBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserCombobox } from '@/components/ui/user-combobox';
import {
  useEmailSettings,
  useUpsertEmailSettings,
} from '@/hooks/useEmailSettings';
import {
  useGeneralSettings,
  useUpsertGeneralSettings,
} from '@/hooks/useGeneralSettings';
import {
  useMetricsSettings,
  useUpsertMetricsSettings,
} from '@/hooks/useMetricsSettings';
import {
  useRiskCreditSettings,
  useUpsertRiskCreditSettings,
} from '@/hooks/useRiskCreditSettings';
import { useUsersList } from '@/hooks/useUsersData';
import { cn } from '@/lib/utils';
import { useMode } from '@/providers/ModeProvider';

interface AgeingBand {
  id: string;
  name: string;
  startDay: number | '';
  endDay: number | '';
  color: string;
  alertEnabled: boolean;
  assignedCreditController: boolean;
  alertUserId?: string;
}

const DEFAULT_BANDS: AgeingBand[] = [
  {
    id: '1',
    name: 'Current',
    startDay: 0,
    endDay: 30,
    color: '#5eead4', // teal-300
    alertEnabled: false,
    assignedCreditController: false,
  },
  {
    id: '2',
    name: '31-60 Days',
    startDay: 31,
    endDay: 60,
    color: '#2dd4bf', // teal-400
    alertEnabled: false,
    assignedCreditController: false,
  },
  {
    id: '3',
    name: '61-90 Days',
    startDay: 61,
    endDay: 90,
    color: '#14b8a6', // teal-500
    alertEnabled: false,
    assignedCreditController: false,
  },
  {
    id: '4',
    name: '91-120 Days',
    startDay: 91,
    endDay: 120,
    color: '#0d9488', // teal-600
    alertEnabled: false,
    assignedCreditController: false,
  },
  {
    id: '5',
    name: 'Over 120 Days',
    startDay: 121,
    endDay: 999,
    color: '#0f766e', // teal-700
    alertEnabled: false,
    assignedCreditController: false,
  },
];

export function GeneralSettingsPage() {
  const queryClient = useQueryClient();
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const [activeTab, setActiveTab] = useState('dashboard');
  const { isDemo } = useMode();
  const [bands, setBands] = useState<AgeingBand[]>(DEFAULT_BANDS);
  const [dsoTarget, setDsoTarget] = useState<number | ''>(40);
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // Category Keys state
  const [categoryKeys, setCategoryKeys] = useState({
    userSortKey1Label: 'User Sort Key 1',
    userSortKey2Label: 'User Sort Key 2',
    userSortKey3Label: 'User Sort Key 3',
    userSortKey4Label: 'User Sort Key 4',
  });

  // Ageing By toggle state
  const [ageingBy, setAgeingBy] = useState<'due-date' | 'transaction-date'>(
    'due-date',
  );

  // On Stop Notes state
  const [onStopNotesEnabled, setOnStopNotesEnabled] = useState(false);

  // Risk & Credit - Credit Limit Warnings state
  const [creditLimitExceededEnabled, setCreditLimitExceededEnabled] =
    useState(false);
  const [notifyCreditController, setNotifyCreditController] = useState(false);
  const [notifyUserIds, setNotifyUserIds] = useState<string[]>([]);
  const [creditWarningThreshold, setCreditWarningThreshold] =
    useState<number>(80);

  // Email settings state
  const [emailDisplayName, setEmailDisplayName] = useState('');

  // Fetch users for alert assignment
  const { data: users = [] } = useUsersList();

  // Risk & Credit settings from API (mode-aware)
  const { data: riskCreditSettings } = useRiskCreditSettings();
  const upsertRiskCreditSettings = useUpsertRiskCreditSettings();

  // General (customer) settings from API (mode-aware)
  const { data: generalSettings } = useGeneralSettings();
  const upsertGeneralSettings = useUpsertGeneralSettings();

  // Email settings from API (mode-aware)
  const { data: emailSettings } = useEmailSettings();
  const upsertEmailSettings = useUpsertEmailSettings();

  // Metrics settings from API (mode-aware)
  const { data: metricsSettings, isLoading: isMetricsLoading } =
    useMetricsSettings();
  const upsertMetricsSettings = useUpsertMetricsSettings();

  // Sync local state when API data loads (skip if user has unsaved edits)
  useEffect(() => {
    if (riskCreditSettings && !hasChanges) {
      setCreditLimitExceededEnabled(riskCreditSettings.notificationEnabled);
      setNotifyCreditController(riskCreditSettings.notifyCreditController);
      setNotifyUserIds(riskCreditSettings.notifyUserIds.map(String));
      setCreditWarningThreshold(
        Math.round(riskCreditSettings.warningThreshold * 100),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskCreditSettings]);

  useEffect(() => {
    if (generalSettings && !hasChanges) {
      setCategoryKeys(generalSettings.categoryKeys);
      setOnStopNotesEnabled(generalSettings.onStopNotesEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalSettings]);

  useEffect(() => {
    if (emailSettings && !hasChanges) {
      setEmailDisplayName(emailSettings.fromDisplayName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSettings]);

  useEffect(() => {
    if (metricsSettings && !hasChanges) {
      setBands(metricsSettings.bands);
      setDsoTarget(metricsSettings.dsoTarget);
      setAgeingBy(metricsSettings.ageingBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricsSettings]);

  // In live mode, show loading state until metrics settings have loaded
  const isBandsLoading = !isDemo && isMetricsLoading && !hasChanges;

  const validateBands = useCallback((): string[] => {
    const errors: string[] = [];

    if (bands.length < 1 || bands.length > 10) {
      errors.push('You must have between 1 and 10 ageing bands');
    }

    bands.forEach((band, index) => {
      if (!band.name.trim()) {
        errors.push(`Band ${index + 1}: Name is required`);
      }

      if (band.startDay !== '' && band.startDay < 0) {
        errors.push(`Band ${index + 1}: Start day must be 0 or greater`);
      }

      if (
        band.startDay !== '' &&
        band.endDay !== '' &&
        band.endDay <= band.startDay
      ) {
        errors.push(
          `Band ${index + 1}: End day must be greater than start day`,
        );
      }

      // Check for gaps between bands
      if (index > 0) {
        const prevBand = bands[index - 1];
        if (
          prevBand.endDay !== '' &&
          band.startDay !== '' &&
          band.startDay !== prevBand.endDay + 1
        ) {
          errors.push(
            `Gap detected between "${prevBand.name}" and "${band.name}". End day of previous band (${prevBand.endDay}) must connect to start day of next band (${band.startDay})`,
          );
        }
      }
    });

    return errors;
  }, [bands]);

  // Real-time validation
  useEffect(() => {
    const errors = validateBands();
    setValidationErrors(errors);
  }, [bands, dsoTarget, validateBands]);

  const handleAddBand = () => {
    if (bands.length >= 10) {
      toast.error('Maximum of 10 bands allowed');
      return;
    }

    const lastBand = bands[bands.length - 1];
    const lastEndDay = lastBand && lastBand.endDay !== '' ? lastBand.endDay : 0;
    const newBand: AgeingBand = {
      id: Date.now().toString(),
      name: `Band ${bands.length + 1}`,
      startDay: lastBand ? lastEndDay + 1 : 0,
      endDay: lastBand ? lastEndDay + 30 : 30,
      color: '#6b7280', // gray
      alertEnabled: false,
      assignedCreditController: false,
    };

    setBands([...bands, newBand]);
    setHasChanges(true);
  };

  const handleRemoveBand = (id: string) => {
    if (bands.length <= 1) {
      toast.error('At least one band is required');
      return;
    }

    setBands(bands.filter((band) => band.id !== id));
    setHasChanges(true);
  };

  const handleBandChange = (
    id: string,
    field: keyof AgeingBand,
    value: string | number | boolean | undefined,
  ) => {
    setBands(
      bands.map((band) =>
        band.id === id
          ? {
              ...band,
              [field]: value,
            }
          : band,
      ),
    );
    setHasChanges(true);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newBands = [...bands];
    const draggedBand = newBands[draggedIndex];
    newBands.splice(draggedIndex, 1);
    newBands.splice(index, 0, draggedBand);

    setBands(newBands);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleKeyboardReorder = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= bands.length) return;
    const newBands = [...bands];
    const movedBand = newBands[index];
    newBands.splice(index, 1);
    newBands.splice(targetIndex, 0, movedBand);
    setBands(newBands);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (activeTab === 'dashboard') {
      const errors = validateBands();

      if (errors.length > 0) {
        errors.forEach((error) => toast.error(error));
        return;
      }

      if (dsoTarget === '' || dsoTarget <= 0) {
        toast.error('DSO target must be a positive number');
        return;
      }
    }

    try {
      if (activeTab === 'riskCredit') {
        if (
          creditLimitExceededEnabled &&
          !notifyCreditController &&
          notifyUserIds.length === 0
        ) {
          toast.error(
            'At least the credit controller or one specific recipient must be selected when notifications are enabled',
          );
          return;
        }

        await upsertRiskCreditSettings.mutateAsync({
          notificationEnabled: creditLimitExceededEnabled,
          notifyCreditController,
          notifyUserIds: notifyUserIds.map(Number),
          warningThreshold: creditWarningThreshold / 100,
        });
      } else if (activeTab === 'customers') {
        await upsertGeneralSettings.mutateAsync({
          onStopNotesEnabled,
          userSortKey1Label: categoryKeys.userSortKey1Label,
          userSortKey2Label: categoryKeys.userSortKey2Label,
          userSortKey3Label: categoryKeys.userSortKey3Label,
          userSortKey4Label: categoryKeys.userSortKey4Label,
        });
      } else if (activeTab === 'email') {
        if (!emailDisplayName.trim()) {
          toast.error('Display Name is required');
          return;
        }
        await upsertEmailSettings.mutateAsync({
          fromDisplayName: emailDisplayName.trim(),
        });
      } else if (activeTab === 'dashboard') {
        await upsertMetricsSettings.mutateAsync({
          dsoTarget: dsoTarget as number,
          ageingBy,
          bands: bands.map((b) => ({
            id: b.id,
            name: b.name,
            startDay: b.startDay as number,
            endDay: b.endDay as number,
            color: b.color,
            alertEnabled: b.alertEnabled,
            assignedCreditController: b.assignedCreditController,
            alertUserId: b.alertUserId,
          })),
        });
        await queryClient.invalidateQueries({ queryKey: ['aging-bands'] });
      }

      let successMessage = 'Risk & Credit settings saved successfully';
      if (activeTab === 'dashboard') {
        successMessage = 'Dashboard settings saved successfully';
      } else if (activeTab === 'customers') {
        successMessage = 'Customer settings saved successfully';
      } else if (activeTab === 'email') {
        successMessage = 'Email settings saved successfully';
      }
      let description = 'Credit limit warning settings have been updated';
      if (activeTab === 'dashboard') {
        description = "Changes will apply to all users' dashboards";
      } else if (activeTab === 'customers') {
        description = 'Category key labels have been updated';
      } else if (activeTab === 'email') {
        description = 'Email sender display name has been updated';
      }

      toast.success(successMessage, {
        description,
        icon: <CheckCircle2 className="h-5 w-5" />,
      });

      setHasChanges(false);
    } catch {
      toast.error('Failed to save settings', {
        description: 'Please try again or contact support',
      });
    }
  };

  const handleCancel = () => {
    if (activeTab === 'dashboard') {
      setBands(metricsSettings?.bands ?? DEFAULT_BANDS);
      setDsoTarget(metricsSettings?.dsoTarget ?? 40);
      setAgeingBy(metricsSettings?.ageingBy ?? 'due-date');
    } else if (activeTab === 'customers') {
      setCategoryKeys(
        generalSettings?.categoryKeys ?? {
          userSortKey1Label: 'User Sort Key 1',
          userSortKey2Label: 'User Sort Key 2',
          userSortKey3Label: 'User Sort Key 3',
          userSortKey4Label: 'User Sort Key 4',
        },
      );
      setOnStopNotesEnabled(generalSettings?.onStopNotesEnabled ?? false);
    } else if (activeTab === 'riskCredit') {
      setCreditLimitExceededEnabled(
        riskCreditSettings?.notificationEnabled ?? false,
      );
      setNotifyCreditController(
        riskCreditSettings?.notifyCreditController ?? false,
      );
      setNotifyUserIds(riskCreditSettings?.notifyUserIds.map(String) ?? []);
      setCreditWarningThreshold(
        riskCreditSettings?.warningThreshold != null
          ? Math.round(riskCreditSettings.warningThreshold * 100)
          : 80,
      );
    } else if (activeTab === 'email') {
      setEmailDisplayName(emailSettings?.fromDisplayName ?? '');
    }
    setHasChanges(false);
    toast.info('Changes discarded');
  };

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="container max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Settings className="h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold tracking-tight">
                General Settings
              </h1>
            </div>
            <p className="text-muted-foreground">
              Configure system-wide settings and preferences
            </p>
          </div>
          <HelpSupportButton screenId="settings-general" />
        </div>

        {/* Settings Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList>
            <TabsTrigger value="dashboard" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Metrics
            </TabsTrigger>
            <TabsTrigger value="customers" className="gap-2">
              <Users className="h-4 w-4" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="riskCredit" className="gap-2">
              <ShieldAlert className="h-4 w-4" />
              Risk & Credit
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-2">
              <Mail className="h-4 w-4" />
              Email
            </TabsTrigger>
          </TabsList>

          {(activeTab === 'dashboard' ||
            activeTab === 'riskCredit' ||
            activeTab === 'customers' ||
            activeTab === 'email') && <DataModeBadge />}

          <TabsContent value="dashboard" className="space-y-6">
            {/* Info Alert */}
            <Alert>
              <AlertDescription>
                These settings apply globally to all users' dashboards. Changes
                will take effect immediately after saving.
              </AlertDescription>
            </Alert>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  <div className="space-y-1">
                    <p className="font-semibold">
                      Please fix the following issues:
                    </p>
                    <ul className="list-disc list-inside space-y-0.5 text-sm">
                      {validationErrors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">
                        Configured Bands
                      </p>
                      {isBandsLoading ? (
                        <Skeleton className="h-8 w-10" />
                      ) : (
                        <p className="text-2xl font-bold">{bands.length}</p>
                      )}
                    </div>
                    <div className="rounded-full bg-primary/10 p-3">
                      <BarChart3 className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">
                        Active Alerts
                      </p>
                      {isBandsLoading ? (
                        <Skeleton className="h-8 w-10" />
                      ) : (
                        <p className="text-2xl font-bold">
                          {bands.filter((b) => b.alertEnabled).length}
                        </p>
                      )}
                    </div>
                    <div className="rounded-full bg-amber-500/10 p-3">
                      <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">
                        Target DSO
                      </p>
                      {isBandsLoading ? (
                        <Skeleton className="h-8 w-16" />
                      ) : (
                        <p className="text-2xl font-bold">{dsoTarget} days</p>
                      )}
                    </div>
                    <div className="rounded-full bg-teal-500/10 p-3">
                      <TrendingUp className="h-6 w-6 text-teal-600 dark:text-teal-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* DSO Target Configuration */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start gap-6">
                  <div className="flex-1 space-y-4">
                    <div>
                      <CardTitle>DSO Target Configuration</CardTitle>
                      <CardDescription>
                        Set the global Days Sales Outstanding (DSO) target
                        displayed in the Performance Snapshot section
                      </CardDescription>
                    </div>

                    <div className="max-w-xs space-y-2">
                      <Label htmlFor="dso-target">Target DSO (Days)</Label>
                      <Input
                        id="dso-target"
                        type="number"
                        min="1"
                        value={dsoTarget}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDsoTarget(val === '' ? '' : parseInt(val));
                          setHasChanges(true);
                        }}
                        placeholder="Enter target DSO in days"
                      />
                      <p className="text-sm text-muted-foreground">
                        Default: 40 days. Must be a positive integer.
                      </p>
                    </div>
                  </div>

                  {/* DSO Calculation Information Panel */}
                  <Alert variant="info" className="max-w-xl flex-shrink-0">
                    <AlertTitle>How DSO is Calculated</AlertTitle>
                    <AlertDescription>
                      <p className="mb-2">
                        <span className="font-semibold text-base">
                          DSO = (Outstanding Debt ÷ Sales) × Days in Period
                        </span>
                      </p>
                      <p>
                        Shows average days to collect payment. Calculated
                        automatically from Access Financials data.
                      </p>
                    </AlertDescription>
                  </Alert>
                </div>
              </CardHeader>
              <CardContent className="space-y-4"></CardContent>
            </Card>

            {/* Ageing Analysis Bands Configuration */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle>Ageing Analysis Bands Configuration</CardTitle>
                    <CardDescription>
                      Define custom ageing analysis bands for dashboard
                      visualizations (1-10 bands)
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Ageing By:
                      </span>
                      <span
                        className={`text-sm font-medium ${ageingBy === 'due-date' ? 'text-foreground' : 'text-muted-foreground'}`}
                      >
                        Due Date
                      </span>
                      <Switch
                        checked={ageingBy === 'transaction-date'}
                        onCheckedChange={(checked) => {
                          setAgeingBy(checked ? 'transaction-date' : 'due-date');
                          setHasChanges(true);
                        }}
                      />
                      <span
                        className={`text-sm font-medium ${ageingBy === 'transaction-date' ? 'text-foreground' : 'text-muted-foreground'}`}
                      >
                        Transaction Date
                      </span>
                    </div>
                    <Button
                      onClick={handleAddBand}
                      size="sm"
                      disabled={bands.length >= 10}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Band
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {isBandsLoading ? (
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-12 w-full rounded-lg" />
                      <Skeleton className="h-4 w-80" />
                    </div>
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-40 w-full rounded-lg" />
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Visual Preview */}
                    <div className="space-y-3">
                      <Label className="text-base">Visual Preview</Label>
                      <div className="flex gap-2 h-12 rounded-lg overflow-hidden border">
                        {bands.map((band) => (
                          <div
                            key={band.id}
                            className="flex-1 flex items-center justify-center text-xs font-medium text-white relative group transition-all hover:scale-105"
                            style={{ backgroundColor: band.color }}
                            title={`${band.name}: ${band.startDay}-${band.endDay} days`}
                          >
                            <span className="truncate px-2">{band.name}</span>
                            {band.alertEnabled && (
                              <AlertCircle className="h-3 w-3 absolute top-1 right-1" />
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        This is how your ageing bands will appear in charts and
                        visualizations
                      </p>
                    </div>
                    {/* Bands List */}
                    <ul className="space-y-3 list-none p-0 m-0">
                      {bands.map((band, index) => (
                        <li
                          key={band.id}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          className={cn(
                            'border rounded-lg p-4 space-y-4 bg-card transition-all',
                            draggedIndex === index && 'opacity-50',
                            'hover:border-primary/50 cursor-move hover:shadow-md',
                          )}
                        >
                          {/* Band Header with Number */}
                          <div className="flex items-center gap-2 pb-2 border-b">
                            <Badge variant="outline" className="font-mono">
                              Band {index + 1}
                            </Badge>
                            {band.alertEnabled && (
                              <Badge variant="secondary" className="gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Alert Enabled
                              </Badge>
                            )}
                            <div className="ml-auto flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded border-2 border-white shadow-sm"
                                style={{ backgroundColor: band.color }}
                                title={band.color}
                              />
                            </div>
                          </div>

                          <div className="flex items-start gap-4">
                            {/* Drag Handle */}
                            <div className="flex items-center pt-8">
                              <button
                                type="button"
                                aria-label={`Reorder band ${index + 1}`}
                                onKeyDown={(e) => {
                                  if (e.key === 'ArrowUp' && index > 0) {
                                    e.preventDefault();
                                    handleKeyboardReorder(index, 'up');
                                  } else if (
                                    e.key === 'ArrowDown' &&
                                    index < bands.length - 1
                                  ) {
                                    e.preventDefault();
                                    handleKeyboardReorder(index, 'down');
                                  }
                                }}
                                className="cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-primary rounded"
                              >
                                <GripVertical className="h-5 w-5 text-muted-foreground hover:text-primary" />
                              </button>
                            </div>

                            {/* Band Configuration */}
                            <div className="flex-1 space-y-4">
                              {/* Row 1: Band Name, Start Day, End Day, Color */}
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor={`band-name-${band.id}`}>
                                    Band Name{' '}
                                    <span className="text-destructive">*</span>
                                  </Label>
                                  <Input
                                    id={`band-name-${band.id}`}
                                    value={band.name}
                                    onChange={(e) =>
                                      handleBandChange(
                                        band.id,
                                        'name',
                                        e.target.value,
                                      )
                                    }
                                    placeholder="e.g., Current"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor={`band-start-${band.id}`}>
                                    Start Day{' '}
                                    <span className="text-destructive">*</span>
                                  </Label>
                                  <Input
                                    id={`band-start-${band.id}`}
                                    type="number"
                                    min="0"
                                    value={band.startDay}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      handleBandChange(
                                        band.id,
                                        'startDay',
                                        val === '' ? '' : parseInt(val),
                                      );
                                    }}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor={`band-end-${band.id}`}>
                                    End Day{' '}
                                    <span className="text-destructive">*</span>
                                  </Label>
                                  <Input
                                    id={`band-end-${band.id}`}
                                    type="number"
                                    min="0"
                                    value={band.endDay}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      handleBandChange(
                                        band.id,
                                        'endDay',
                                        val === '' ? '' : parseInt(val),
                                      );
                                    }}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor={`band-color-${band.id}`}>
                                    Color{' '}
                                    <span className="text-destructive">*</span>
                                  </Label>
                                  <div className="flex gap-2">
                                    <Input
                                      id={`band-color-${band.id}`}
                                      type="color"
                                      value={band.color}
                                      onChange={(e) =>
                                        handleBandChange(
                                          band.id,
                                          'color',
                                          e.target.value,
                                        )
                                      }
                                      className="w-20 cursor-pointer"
                                    />
                                    <div
                                      className="flex-1 rounded-md border"
                                      style={{ backgroundColor: band.color }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Remove Button */}
                            <div className="flex items-center pt-8">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveBand(band.id)}
                                disabled={bands.length <= 1}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>

                    {/* Band Count Info */}
                    <div className="flex items-center justify-between text-sm text-muted-foreground pt-2">
                      <span>{bands.length} of 10 bands configured</span>
                      <span>Drag bands to reorder</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={!hasChanges}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={
                  !hasChanges ||
                  dsoTarget === '' ||
                  bands.some((b) => b.startDay === '' || b.endDay === '')
                }
              >
                Save Changes
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="customers" className="space-y-6">
            {/* Info Alert */}
            <Alert>
              <AlertDescription>
                These settings apply to all customer interactions. Changes will
                take effect immediately after saving.
              </AlertDescription>
            </Alert>

            {/* On Stop Notes Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>On Stop Notes</CardTitle>
                <CardDescription>
                  Configure whether users are prompted to add a note when
                  placing a customer on stop
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="onStopNotes" className="text-base">
                      Require Note When Placing Customer On Stop
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      When enabled, users will be prompted to add a note
                      explaining why a customer is being placed on stop. The
                      note will be automatically saved to the customer's Notes
                      tab.
                    </p>
                  </div>
                  <Switch
                    id="onStopNotes"
                    checked={onStopNotesEnabled}
                    onCheckedChange={(checked) => {
                      setOnStopNotesEnabled(checked);
                      setHasChanges(true);
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Category Keys Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Category Keys</CardTitle>
                <CardDescription>
                  Customize the labels for User Sort Key fields displayed in
                  Customer Details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="userSortKey1Label">
                      User Sort Key 1 Label
                    </Label>
                    <Input
                      id="userSortKey1Label"
                      value={categoryKeys.userSortKey1Label}
                      onChange={(e) => {
                        setCategoryKeys({
                          ...categoryKeys,
                          userSortKey1Label: e.target.value,
                        });
                        setHasChanges(true);
                      }}
                      placeholder="Enter custom label"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userSortKey2Label">
                      User Sort Key 2 Label
                    </Label>
                    <Input
                      id="userSortKey2Label"
                      value={categoryKeys.userSortKey2Label}
                      onChange={(e) => {
                        setCategoryKeys({
                          ...categoryKeys,
                          userSortKey2Label: e.target.value,
                        });
                        setHasChanges(true);
                      }}
                      placeholder="Enter custom label"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userSortKey3Label">
                      User Sort Key 3 Label
                    </Label>
                    <Input
                      id="userSortKey3Label"
                      value={categoryKeys.userSortKey3Label}
                      onChange={(e) => {
                        setCategoryKeys({
                          ...categoryKeys,
                          userSortKey3Label: e.target.value,
                        });
                        setHasChanges(true);
                      }}
                      placeholder="Enter custom label"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userSortKey4Label">
                      User Sort Key 4 Label
                    </Label>
                    <Input
                      id="userSortKey4Label"
                      value={categoryKeys.userSortKey4Label}
                      onChange={(e) => {
                        setCategoryKeys({
                          ...categoryKeys,
                          userSortKey4Label: e.target.value,
                        });
                        setHasChanges(true);
                      }}
                      placeholder="Enter custom label"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={!hasChanges}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges}>
                Save Changes
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="riskCredit" className="space-y-6">
            {/* Info Alert */}
            <Alert>
              <AlertDescription>
                Configure credit limit monitoring and notification settings.
                These settings help you proactively manage customer credit risk.
              </AlertDescription>
            </Alert>

            {/* Credit Limit Warnings Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Credit Limit Warnings</CardTitle>
                <CardDescription>
                  Configure notifications for when customers exceed or approach
                  their credit limits
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Credit Limit Exceeded Notification */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-4 border-b">
                    <div className="space-y-0.5">
                      <Label
                        htmlFor="creditLimitExceeded"
                        className="text-base"
                      >
                        Credit Limit Exceeded Notification
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Send feed notifications when a customer exceeds their
                        credit limit
                      </p>
                    </div>
                    <Switch
                      id="creditLimitExceeded"
                      checked={creditLimitExceededEnabled}
                      onCheckedChange={(checked) => {
                        setCreditLimitExceededEnabled(checked);
                        setHasChanges(true);
                      }}
                    />
                  </div>

                  {/* Recipient Configuration - Only shown when toggle is ON */}
                  {creditLimitExceededEnabled && (
                    <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                      <div className="space-y-3">
                        <Label className="text-base">
                          Notification Recipients
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Choose who should receive credit limit exceeded
                          notifications. You can select both options.
                        </p>

                        {/* Credit Controller Checkbox */}
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="notify-credit-controller"
                            checked={notifyCreditController}
                            onCheckedChange={(checked) => {
                              setNotifyCreditController(checked === true);
                              setHasChanges(true);
                            }}
                          />
                          <Label
                            htmlFor="notify-credit-controller"
                            className="font-normal cursor-pointer"
                          >
                            Assigned Credit Controller for that customer
                          </Label>
                        </div>

                        {/* Specific Recipients Section */}
                        <div className="space-y-2">
                          <Label>Specific Recipients</Label>
                          <UserCombobox
                            users={users.filter(
                              (x) => !notifyUserIds.includes(x.id),
                            )}
                            value=""
                            onValueChange={(userId) => {
                              if (userId && !notifyUserIds.includes(userId)) {
                                setNotifyUserIds([...notifyUserIds, userId]);
                                setHasChanges(true);
                              }
                            }}
                            placeholder="Add a user to receive notifications..."
                            emptyText="No users found"
                          />

                          {/* Selected Users List */}
                          {notifyUserIds.length > 0 && (
                            <div className="space-y-1 mt-2">
                              {notifyUserIds.map((userId) => {
                                const user = users.find((x) => x.id === userId);
                                if (!user) return null;
                                return (
                                  <div
                                    key={userId}
                                    className="flex items-center justify-between rounded-md border px-3 py-2"
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <div className="flex flex-col min-w-0">
                                        <span className="text-sm font-medium truncate">
                                          {user.firstName} {user.lastName}
                                        </span>
                                        <span className="text-xs text-muted-foreground truncate">
                                          {user.email}
                                        </span>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                      onClick={() => {
                                        setNotifyUserIds(
                                          notifyUserIds.filter(
                                            (x) => x !== userId,
                                          ),
                                        );
                                        setHasChanges(true);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <p className="text-xs text-muted-foreground">
                            These users will receive all credit limit exceeded
                            notifications
                          </p>
                        </div>
                      </div>

                      {/* Notification Message Preview */}
                      <Alert className="bg-muted/50">
                        <AlertTitle>Notification Message Preview</AlertTitle>
                        <AlertDescription>
                          <p className="text-sm mt-2">
                            <span className="font-semibold">
                              [Customer Name]
                            </span>{' '}
                            has exceeded their credit limit.
                          </p>
                          <p className="text-sm mt-1">
                            Credit Limit:{' '}
                            <span className="font-semibold">£10,000.00</span> |
                            Current Outstanding:{' '}
                            <span className="font-semibold text-destructive">
                              £12,500.00
                            </span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Clicking the notification will navigate directly to
                            the customer's profile
                          </p>
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </div>

                {/* Credit Warning Threshold */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label
                      htmlFor="creditWarningThreshold"
                      className={cn(
                        'text-base',
                        !creditLimitExceededEnabled && 'text-muted-foreground',
                      )}
                    >
                      Credit Warning Threshold
                    </Label>
                    <p
                      className={cn(
                        'text-sm text-muted-foreground',
                        !creditLimitExceededEnabled &&
                          'text-muted-foreground/60',
                      )}
                    >
                      Set the percentage of credit limit utilization that
                      triggers an early warning notification
                    </p>
                  </div>

                  <div className="max-w-xs space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        id="creditWarningThreshold"
                        type="number"
                        min="1"
                        max="100"
                        value={creditWarningThreshold}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          setCreditWarningThreshold(
                            Math.min(100, Math.max(1, value)),
                          );
                          setHasChanges(true);
                        }}
                        disabled={!creditLimitExceededEnabled}
                        className="w-24"
                      />
                      <span
                        className={cn(
                          'text-sm font-medium',
                          !creditLimitExceededEnabled &&
                            'text-muted-foreground',
                        )}
                      >
                        %
                      </span>
                    </div>
                    <p
                      className={cn(
                        'text-xs text-muted-foreground',
                        !creditLimitExceededEnabled &&
                          'text-muted-foreground/60',
                      )}
                    >
                      Default: 80%. Notifications will be sent to the same
                      recipient configured above.
                    </p>
                  </div>

                  {creditLimitExceededEnabled && (
                    <Alert variant="info" className="max-w-2xl">
                      <AlertDescription>
                        <p className="text-sm">
                          <span className="font-semibold">Example:</span> With a
                          threshold of {creditWarningThreshold}%, a customer
                          with a £10,000 credit limit will trigger a warning
                          notification when their outstanding balance reaches £
                          {(
                            (creditWarningThreshold / 100) *
                            10000
                          ).toLocaleString('en-GB', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                          .
                        </p>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={!hasChanges}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges}>
                Save Changes
              </Button>
            </div>
          </TabsContent>
          {/* Email Settings Tab */}
          <TabsContent value="email" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Email Sender Settings</CardTitle>
                <CardDescription>
                  Configure the sender display name used for outgoing emails.
                  The sender email address is managed by the system.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="max-w-md space-y-2">
                  <Label htmlFor="from-email">Sender Email Address</Label>
                  <Input
                    id="from-email"
                    value={emailSettings?.fromEmail ?? ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    This address is set by the system and cannot be changed.
                  </p>
                </div>

                <div className="max-w-md space-y-2">
                  <Label htmlFor="from-display-name">Sender Display Name</Label>
                  <Input
                    id="from-display-name"
                    value={emailDisplayName}
                    onChange={(e) => {
                      setEmailDisplayName(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="Enter display name for outgoing emails"
                  />
                  <p className="text-xs text-muted-foreground">
                    This name will appear as the sender in recipients' inboxes.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={!hasChanges}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges}>
                Save Changes
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
